/**
 * Notification Types
 *
 * Types for the notification system including in-app notifications,
 * email preferences, and notification categories.
 */

import type { BaseEntity, UUID } from './common.types'

/**
 * Notification topic categories used for per-channel preference configuration.
 *
 * @remarks
 * Each category maps to a toggle group in the notification preferences UI.
 * Role-specific categories (`parent_alerts`, `school_admin`, `vendor`) are
 * only shown to users with the matching role.
 */
export type NotificationCategory =
  | 'challenge_updates'    // Challenge status changes, new assignments
  | 'social_activity'      // Comments, thumbs up, community activity
  | 'community_activity'   // New posts, member activity
  | 'reminders'            // Due dates, incomplete challenges
  | 'digests'              // Daily/weekly summaries
  | 'system'               // Account, security, platform updates
  | 'gifts'                // Gift received, gift reminders
  | 'verification'         // TR approved/rejected
  | 'teacher_feedback'     // Teacher comments on TRs
  | 'gamification'         // XP gains, level ups, badge earned
  | 'parent_alerts'        // Parent-specific: child activity, consent requests
  | 'school_admin'         // SA-specific: roster changes, compliance alerts
  | 'vendor'               // Vendor-specific: challenge performance, payouts

/**
 * Delivery channel through which a notification can be sent.
 */
export type NotificationChannel = 'in_app' | 'email' | 'push'

/**
 * Urgency level of a notification. `critical` notifications bypass quiet hours.
 */
export type NotificationPriority = 'low' | 'medium' | 'high' | 'critical'

/**
 * A single in-app notification delivered to a user.
 *
 * @remarks
 * Notifications are always stored in-app. Depending on user preferences, they
 * may also be sent via email and/or push. The `relatedType` and `relatedId` fields
 * enable deep-linking to the relevant entity from the notification bell dropdown.
 *
 * @example
 * ```ts
 * const notification: Notification = {
 *   id: 'notif-001',
 *   createdAt: '2025-10-06T09:05:00Z',
 *   updatedAt: '2025-10-06T09:05:00Z',
 *   userId: 'usr-042',
 *   category: 'verification',
 *   priority: 'high',
 *   title: 'Track Record Approved!',
 *   message: 'Your "Build a Birdhouse" Track Record has been verified.',
 *   actionUrl: '/track-records/tr-001',
 *   relatedType: 'track_record',
 *   relatedId: 'tr-001',
 *   isRead: false,
 *   sentViaEmail: true,
 *   sentViaPush: false,
 * }
 * ```
 */
export interface Notification extends BaseEntity {
  userId: UUID
  category: NotificationCategory
  priority: NotificationPriority

  title: string
  message: string

  /** Deep-link URL to the related content */
  actionUrl?: string
  /** Button label for the deep-link CTA (e.g., "View Track Record") */
  actionLabel?: string

  /** Type of entity this notification relates to (for deep-linking) */
  relatedType?: 'challenge' | 'track_record' | 'community' | 'user' | 'gift' | 'assignment'
  /** UUID of the related entity */
  relatedId?: UUID

  isRead: boolean
  readAt?: string

  /** Whether this notification was also sent via email */
  sentViaEmail: boolean
  /** Whether this notification was also sent via push */
  sentViaPush: boolean
}

/**
 * Full notification preference configuration with per-category, per-channel granularity.
 *
 * @remarks
 * Master toggles (`inAppEnabled`, `emailEnabled`, `pushEnabled`) act as global
 * kill switches -- when a channel is disabled at the master level, all category
 * preferences for that channel are effectively off. Quiet hours suppress non-critical
 * notifications during the specified time window.
 */
export interface NotificationPreferences {
  id?: string  // Added by storage
  createdAt?: string  // Added by storage
  updatedAt?: string  // Added by storage
  userId: UUID

  /** Master toggle -- disables all in-app notifications when false */
  inAppEnabled: boolean
  /** Master toggle -- disables all email notifications when false */
  emailEnabled: boolean
  /** Master toggle -- disables all push notifications when false */
  pushEnabled: boolean

  /** Per-category, per-channel toggles */
  categoryPreferences: {
    [key in NotificationCategory]: {
      inApp: boolean
      email: boolean
      push: boolean
    }
  }

  /** Whether quiet hours are active */
  quietHoursEnabled: boolean
  /** Quiet hours start time in HH:mm format */
  quietHoursStart: string
  /** Quiet hours end time in HH:mm format */
  quietHoursEnd: string
  /** IANA timezone for quiet hours (e.g., "America/New_York") */
  quietHoursTimezone: string

  /** How frequently email notifications are batched and sent */
  emailDigestFrequency: 'immediate' | 'daily' | 'weekly' | 'never'
}

/**
 * Factory defaults for new user notification preferences.
 * In-app and email are enabled for most categories; push is disabled by default.
 */
export const defaultNotificationPreferences: Omit<NotificationPreferences, 'userId'> = {
  inAppEnabled: true,
  emailEnabled: true,
  pushEnabled: false,

  categoryPreferences: {
    challenge_updates: { inApp: true, email: true, push: false },
    social_activity: { inApp: true, email: false, push: false },
    community_activity: { inApp: true, email: false, push: false },
    reminders: { inApp: true, email: true, push: false },
    digests: { inApp: false, email: true, push: false },
    system: { inApp: true, email: true, push: false },
    gifts: { inApp: true, email: true, push: false },
    verification: { inApp: true, email: true, push: false },
    teacher_feedback: { inApp: true, email: true, push: false },
    gamification: { inApp: true, email: false, push: false },
    parent_alerts: { inApp: true, email: true, push: false },
    school_admin: { inApp: true, email: true, push: false },
    vendor: { inApp: true, email: true, push: false },
  },

  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  quietHoursTimezone: 'America/New_York',

  emailDigestFrequency: 'daily',
}

/**
 * Aggregate notification counts used by the notification bell badge and filter sidebar.
 */
export interface NotificationCounts {
  total: number
  unread: number
  /** Unread count broken down by category (categories with zero unread may be omitted) */
  byCategory: Partial<Record<NotificationCategory, number>>
}

// ============================================================================
// COPPA Age-Based Notification Restrictions
// ============================================================================

/**
 * Notification categories completely blocked for under-13 users.
 * These categories are never delivered (in-app, email, or push).
 */
export const COPPA_BLOCKED_CATEGORIES_UNDER_13: NotificationCategory[] = [
  'social_activity',
  'community_activity',
  'vendor',
]

/**
 * For under-13 users, only these categories may be sent via email.
 * All other categories are in-app only.
 */
export const COPPA_EMAIL_ALLOWED_UNDER_13: NotificationCategory[] = [
  'system',
  'parent_alerts',
]

/**
 * For 13-17 users, these categories may be sent via email.
 * Social/community email delivery is restricted for minors.
 */
export const COPPA_EMAIL_ALLOWED_13_17: NotificationCategory[] = [
  'system',
  'parent_alerts',
  'challenge_updates',
  'verification',
  'teacher_feedback',
  'reminders',
  'gifts',
  'gamification',
]

/**
 * COPPA-safe default notification preferences for under-13 users.
 * Blocks social/community/vendor categories entirely, restricts email.
 */
export const coppaDefaultPreferencesUnder13: Omit<NotificationPreferences, 'userId'> = {
  inAppEnabled: true,
  emailEnabled: true,
  pushEnabled: false,

  categoryPreferences: {
    challenge_updates: { inApp: true, email: false, push: false },
    social_activity: { inApp: false, email: false, push: false },
    community_activity: { inApp: false, email: false, push: false },
    reminders: { inApp: true, email: false, push: false },
    digests: { inApp: false, email: false, push: false },
    system: { inApp: true, email: true, push: false },
    gifts: { inApp: true, email: false, push: false },
    verification: { inApp: true, email: false, push: false },
    teacher_feedback: { inApp: true, email: false, push: false },
    gamification: { inApp: true, email: false, push: false },
    parent_alerts: { inApp: true, email: true, push: false },
    school_admin: { inApp: false, email: false, push: false },
    vendor: { inApp: false, email: false, push: false },
  },

  quietHoursEnabled: true,
  quietHoursStart: '20:00',
  quietHoursEnd: '08:00',
  quietHoursTimezone: 'America/New_York',

  emailDigestFrequency: 'never',
}
